<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { IPagination } from '../../types/pagination.type';
import { useToast } from '../../composables/useToast';
import TypeTransportsRepository from '@repositories/typeTransports.repository';
import { ITypeTransport } from '../../types/typeTransport.type';
import { useI18n } from "vue-i18n";
import DataTable from "@components/DataTable.vue";

const isLoadingTypeTransports = ref<boolean>(false);

const { t } = useI18n();
const { $toast } = useToast();
const typeTransports = ref<ITypeTransport[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedTypeTransport = ref<ITypeTransport | null>(null);
const isViewActive = ref<boolean>(true);
const searchFormRef = ref<any>(null);

const fieldsTableInactive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'typeName',
    label: t('global.nombre'),
  },
  {
    key: 'restore',
    label: t('global.restaurar'),
  },
]);

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: 'Id',
    sortable: true,
  },
  {
    key: 'typeName',
    label: t('global.nombre'),
  },
  {
    key: 'typeTransportInfo',
    label: t('global.editar'),
  },
  {
    key: 'delete',
    label: t('global.eliminar'),
  },
]);

const fieldsFilter: any = ref([
  {
    key: 'search',
    title: t('global.buscar'),
    type: 'text',
    placeholder: t('ajustes.transportes.buscar_por_nombre'),
    value: null,
    cols: 3,
  },
]);

// API
const getTypeTransportsFromAPI = async (
  page: number,
  deleted: boolean = false,
  params = {},
) => {
  isLoadingTypeTransports.value = true;
  try {
    const { data } = await TypeTransportsRepository.getTypeTransportsProPaginated({
      page,
      deleted,
      ...params,
    });
    if (data && Array.isArray(data?.typesTransports)) {
      paginationData.value = data.pagination;
      updateTypeTransports(data.typesTransports);
      return data.typesTransports;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching type transports:', error);
    return [];
  } finally {
    isLoadingTypeTransports.value = false;
  }
};

const updateTypeTransports = (newTypeTransports: ITypeTransport[]) => {
  typeTransports.value = newTypeTransports;
};

const loadTypeTransports = async () => {
  await getTypeTransportsFromAPI(1);
};

onMounted(() => {
  loadTypeTransports();
});

// Create inputs from modals
const fieldsInputTypeTransports = computed(() => {
  return [
    {
      key: 'typeName',
      title: t('ajustes.transportes.nombre_tipo_transporte'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
      },
      placeholder: t('ajustes.transportes.nombre_tipo_transporte'),
      value: selectedTypeTransport.value?.typeName,
      cols: 12,
    },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getTypeTransportsFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.transportes.agregar_tipo_transporte');
  isModalOpen.value = true;
  modalType.value = 'modalCreateTypeTransport';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedTypeTransport.value = null;
};

const handleActive = async () => {
  const typeTransportsData = await getTypeTransportsFromAPI(1);
  updateTypeTransports(typeTransportsData);
  isViewActive.value = true;
};

const handleInactive = async () => {
  const typeTransportsDeleted = await getTypeTransportsFromAPI(1, true);
  updateTypeTransports(typeTransportsDeleted);
  isViewActive.value = false;
};

const handleOpenModalDialog = (item: ITypeTransport) => {
  modalTitle.value = isViewActive.value
    ? `${t('ajustes.transportes.msg_borrar_tipo_transporte')} ${item.id}`
    : `${t('ajustes.transportes.msg_restaurar_tipo_transporte')} ${item.id}`;
  modalType.value = 'modalDialog';
  selectedTypeTransport.value = item;
  isModalOpen.value = true;
};

// Filter Functions
const handleOnSubmit = async ({ items }: any) => {
  getTypeTransportsFromAPI(1, false, items);
};

const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  getTypeTransportsFromAPI(1, false);
};

// CRUD
const handleCreateTypeTransport = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  try {
    const dataToSend = {
      ...items,
    };
    const data = await TypeTransportsRepository.createTypeTransport(dataToSend);
    $toast?.success(t('ajustes.transportes.msg_agregar_tipo_transporte'));
  } catch (error: any) {
    if (error?.message) {
      $toast?.error(error.message);
      return;
    }
    $toast?.error(t('ajustes.transportes.msg_error_creacion'));
  }
  getTypeTransportsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleSelectTypeTransport = (typeTransportData: ITypeTransport) => {
  modalTitle.value = t('ajustes.transportes.actualizar_tipo_transporte');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateTypeTransport');
  selectedTypeTransport.value = typeTransportData;
};

const handleUpdateTypeTransport = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedTypeTransport.value) return;
  const typeTransportForUpdate = {
    ...items,
    id: selectedTypeTransport.value.id,
  };

  try {
    await TypeTransportsRepository.updateTypeTransport(typeTransportForUpdate);
    $toast?.success(t('ajustes.transportes.msg_actualizado'));
  } catch (error: any) {
    if (error?.message) {
      $toast?.error(error.message);
      return;
    }
    $toast?.error(t('ajustes.transportes.msg_error_actualizacion'));
  }
  getTypeTransportsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
  handleClose();
};

const handleDeleteRestoreTypeTransport = async () => {
  if (selectedTypeTransport?.value?.id) {
    let response = null;
    try {
      if (isViewActive.value) {
        response = await TypeTransportsRepository.deleteTypeTransport(
          selectedTypeTransport.value.id,
        );
      } else {
        response = await TypeTransportsRepository.restoreTypeTransport(
          selectedTypeTransport.value.id,
        );
      }
    } catch (error: any) {
      if (isViewActive.value) {
        return $toast?.error(t('ajustes.transportes.msg_error_eliminar'));
      }
      if (error?.message) {
        $toast?.error(error.message);
        return;
      }
      $toast?.error(t('ajustes.transportes.msg_error_restaurar'));
    }
    getTypeTransportsFromAPI(paginationData.value?.current_page ?? 1, !isViewActive.value);
    handleClose();
  }
};
</script>

<template>
  <Layout title="Tipos de Transporte" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-between">
        <div>
          <CButton
            class="background-blue button__form"
            style="padding: 0.5rem !important; margin-right: 15px"
            type="button"
            @click="handleActive"
          >
            <template #content>{{ t('global.activos') }}</template>
          </CButton>
          <CButton
            class="background-pink button__form"
            style="padding: 0.5rem !important"
            type="button"
            @click="handleInactive"
          >
            <template #content>{{ t('global.inactivos') }}</template>
          </CButton>
        </div>
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>{{ t('ajustes.transportes.agregar_tipo_transporte') }}</template>
        </CButton>
      </div>

      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          :inputs="fieldsFilter"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content>{{ t('global.buscar') }}</template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content>{{ t('global.limpiar_filtro') }}</template>
            </CButton>
          </template>
        </Form>
      </div>

      <DataTable
        :loading="isLoadingTypeTransports"
        :fields="isViewActive ? fieldsTableActive : fieldsTableInactive"
        :items="typeTransports"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(typeTransportInfo)="typeTransportData">
          <div
            @click="handleSelectTypeTransport(typeTransportData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-slot:cell(delete)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/basura.svg" alt="eliminar" />
          </div>
        </template>
        <template v-slot:cell(restore)="operationsData">
          <div
            @click="handleOpenModalDialog(operationsData.data.item)"
            class="button-icon restore"
          >
            <img src="/icons/restaurar.svg" alt="restore" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateTypeTransport'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeTransports"
            @onSubmit="handleCreateTypeTransport"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('ajustes.transportes.agregar_tipo_transporte') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateTypeTransport'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputTypeTransports"
            @onSubmit="handleUpdateTypeTransport"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{ t('global.actualizar') }}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalDialog'">
          <h5 class="text-center">
            {{
              isViewActive
                ? `${t('ajustes.transportes.seguro_eliminar')} ${selectedTypeTransport?.typeName}?`
                : `${t('ajustes.transportes.seguro_restaurar')} ${selectedTypeTransport?.typeName}?`
            }}
          </h5>
          <div class="d-flex mt-3">
            <CButton
              class="w-100 fs-6 p-2"
              background="background-pink"
              @click="handleDeleteRestoreTypeTransport"
            >
              <template #content>
                <div>
                  <span>{{ t('global.confirmar') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
